import React, { useState, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Context para proveer handleClose a componentes hijos
const GlassDialogContext = createContext<{
  handleClose: () => void;
} | null>(null);

export const useGlassDialog = () => {
  const context = useContext(GlassDialogContext);
  return context;
};

interface GlassDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  hideCloseButton?: boolean;
}

export const GlassDialog = ({
  open,
  onOpenChange,
  children,
  className,
  hideCloseButton = false,
}: GlassDialogProps) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    if (isClosing) return; // Prevent double-click

    setIsClosing(true);

    // Wait for blur animation to finish before calling onOpenChange
    setTimeout(() => {
      if (onOpenChange) {
        onOpenChange(false);
      }
      setIsClosing(false);
    }, 300);
  };

  useEffect(() => {
    if (!open) {
      setIsClosing(false);
    }
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[9999] overflow-hidden">
          {/* Backdrop with Framer Motion blur */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{
              opacity: 0,
              backdropFilter: 'blur(0px)',
            }}
            animate={{
              opacity: isClosing ? 0 : 1,
              backdropFilter: isClosing ? 'blur(0px)' : 'blur(24px)',
            }}
            exit={{
              opacity: 0,
              backdropFilter: 'blur(0px)',
            }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
            onClick={handleClose}
          >
            {/* Glass elements */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-white/2" />
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white/5 to-transparent" />
          </motion.div>

          {/* Dialog content */}
          <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-8">
            <motion.div
              className={cn(
                'relative w-full',
                'backdrop-blur-2xl bg-gradient-to-br from-white/10 to-white/5',
                'border border-white/20',
                'rounded-3xl shadow-2xl',
                'overflow-hidden',
                className,
              )}
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{
                opacity: isClosing ? 0 : 1,
                scale: isClosing ? 0.95 : 1,
                y: isClosing ? 30 : 0,
              }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              transition={{
                duration: 0.3,
                ease: 'easeOut',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Glass highlight overlay */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-transparent via-white/5 to-white/15 pointer-events-none" />

              {/* Border glow effect */}
              <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-br from-white/20 via-transparent to-white/10 -z-10" />

              {/* Close button */}
              {!hideCloseButton && onOpenChange && (
                <motion.button
                  onClick={handleClose}
                  className="absolute top-4 right-4 z-10 p-2.5 rounded-full backdrop-blur-xl bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 transition-all duration-200 group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-4 h-4 text-white/70 group-hover:text-white/90 transition-colors" />
                </motion.button>
              )}

              {/* Content with context */}
              <GlassDialogContext.Provider value={{ handleClose }}>
                <div className="relative p-4">{children}</div>
              </GlassDialogContext.Provider>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

interface GlassDialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export const GlassDialogContent = ({ children, className }: GlassDialogContentProps) => {
  return <div className={cn('space-y-5', className)}>{children}</div>;
};

interface GlassDialogHeaderProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  className?: string;
}

export const GlassDialogHeader = ({
  icon,
  title,
  description,
  className,
}: GlassDialogHeaderProps) => {
  return (
    <motion.div
      className={cn('text-center', className)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {icon && (
        <div className="flex justify-center mb-4">
          <div className="p-3 rounded-2xl backdrop-blur-md bg-slate-900 border border-white/20">
            {icon}
          </div>
        </div>
      )}
      <h3 className="text-xl font-inter font-light text-white/95 mb-2 tracking-wide">{title}</h3>
      {description && (
        <p className="text-sm text-white/60 font-inter font-light tracking-wide">{description}</p>
      )}
    </motion.div>
  );
};

interface GlassDialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const GlassDialogFooter = ({ children, className }: GlassDialogFooterProps) => {
  return (
    <motion.div
      className={cn('flex flex-col gap-3', className)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
    >
      {children}
    </motion.div>
  );
};
